// The river (2026-10-26, graphics pass checkpoint 1): a painted moat that rings the center, crossed by a stone bridge on
// each of the four roads in. Visual only: the sim's map has no river. The water and its banks sit on forest cells the
// sim already blocks, and where it crosses a road a bridge deck covers every cell the channel sinks, so a soldier never
// stands over water. Pure (no three, no DOM) so tests/look.test.ts can hold it to the sim's map.
import { SIZE } from '../sim/sim';

const CX = SIZE / 2, CZ = SIZE / 2;
/** Channel depth at its middle, the bank's width past the water, and where the water's surface sits. */
export const RIVER = { depth: 1.1, bank: 1.3, water: -0.5 };
/** The deck's rise at its middle; soldiers crossing ride it (liftAt). A real hump (2026-10-26: at 0.55 over a 12 m deck
 *  the bridges read as flat paved squares). */
export const HUMP = 1.0;
/** How far each deck runs on past the last road cell the channel sinks: its ramps, so a bridge reads long, not square. */
export const RAMP = 2.6;
/** Board mesh vertices per meter (board.ts): a cell sinks when any of its vertices does. */
export const GRID = 2;

/** The river's middle, as a distance from the center, at angle a: closest in on the four roads (cos 4a is -1 on the
 *  diagonals) so it meets each road square on, wider out in the forest between, with a slow wobble so it reads drawn
 *  by hand, not ruled. The 30 m base keeps the ramps and end-post caps outside the 19.5 m plaza. */
export const riverMid = (a: number) => 30 + 2 * Math.cos(4 * a) + 0.6 * Math.sin(9 * a + 0.7);
/** Half the water's width at angle a. */
export const riverHalf = (a: number) => 2.1 + 0.3 * Math.sin(5 * a + 2);

/** How far below the board the ground sits at x, z (0 off the channel). */
export function depthAt(x: number, z: number) {
  const a = Math.atan2(z - CZ, x - CX), d = Math.abs(Math.hypot(x - CX, z - CZ) - riverMid(a)), hw = riverHalf(a);
  const t = (hw + RIVER.bank - d) / (RIVER.bank + 0.6); // 0 at the bank's top, 1 a little inside the water's edge
  return t <= 0 ? 0 : RIVER.depth * (t >= 1 ? 1 : t * t * (3 - 2 * t));
}

/** A bridge: its middle, its axis along the road (ux, uz; v is across), and the deck's half length and half width. */
export type Bridge = { x: number; z: number; ux: number; uz: number; hl: number; hw: number };

/** One bridge per road in, sized to cover every road cell the channel sinks (sunkRoadCells): as wide as the road
 *  there (centered on it) and longer by its ramps. */
export function bridgesFor(road: Uint8Array): Bridge[] {
  return [1, 3, 5, 7].map((k) => {
    const a = (k * Math.PI) / 4, ux = Math.cos(a), uz = Math.sin(a), r = riverMid(a);
    const x = CX + ux * r, z = CZ + uz * r;
    let u0 = 0, u1 = 0, v0 = 0, v1 = 0;
    for (const [i, j] of sunkRoadCells(road)) {
      const dx = i + 0.5 - x, dz = j + 0.5 - z;
      if (Math.hypot(dx, dz) > 14) continue; // another road's crossing
      for (const [ox, oz] of [[-0.5, -0.5], [0.5, -0.5], [-0.5, 0.5], [0.5, 0.5]]) {
        const u = (dx + ox) * ux + (dz + oz) * uz, v = -(dx + ox) * uz + (dz + oz) * ux;
        u0 = Math.min(u0, u); u1 = Math.max(u1, u); v0 = Math.min(v0, v); v1 = Math.max(v1, v);
      }
    }
    // along the road centered on the river's middle, so the hump peaks over the water; across, centered on the road
    const vc = (v0 + v1) / 2;
    return { x: x - uz * vc, z: z + ux * vc, ux, uz, hl: Math.max(-u0, u1) + RAMP, hw: (v1 - v0) / 2 + 0.5 }; // the walls (board.ts) stand outside the road
  });
}

/** Whether the board's surface over cell i, j dips at all: any of the mesh's vertices on it below 0. */
export function cellSinks(i: number, j: number) {
  for (let b = 0; b <= GRID; b++) for (let a = 0; a <= GRID; a++) if (depthAt(i + a / GRID, j + b / GRID) > 0) return true;
  return false;
}

/** Road cells the channel would sink. */
export function sunkRoadCells(road: Uint8Array): [number, number][] {
  const out: [number, number][] = [];
  for (let j = 0; j < SIZE; j++) for (let i = 0; i < SIZE; i++) if (road[j * SIZE + i] && cellSinks(i, j)) out.push([i, j]);
  return out;
}

/** Where x, z sits in a bridge's frame: along (u) and across (v), or null off every deck. */
export function onBridge(bridges: Bridge[], x: number, z: number) {
  for (const b of bridges) {
    const dx = x - b.x, dz = z - b.z, u = dx * b.ux + dz * b.uz, v = -dx * b.uz + dz * b.ux;
    if (Math.abs(u) <= b.hl && Math.abs(v) <= b.hw) return { b, u, v };
  }
  return null;
}

/** The deck's height along its length: HUMP in the middle, easing to 0 and level at both ends (a bell, so the ramps
 *  meet the road without a kink). */
export const deckY = (u: number, hl: number) => (Math.abs(u) >= hl ? 0 : HUMP * (0.5 + 0.5 * Math.cos((Math.PI * u) / hl)));

/** How high a soldier at x, z stands: on a deck, its hump; anywhere else, the board. */
export function liftAt(bridges: Bridge[], x: number, z: number) {
  const at = onBridge(bridges, x, z);
  return at ? deckY(at.u, at.b.hl) : 0;
}

/** The highest deck under a ring of radius r at x, z (its middle and 16 points round it), so a ring on the ground
 *  (a duel, an arrow storm, a cast) lies over a bridge instead of under it (Astra, 2026-10-26). */
export function liftUnder(bridges: Bridge[], x: number, z: number, r: number) {
  let y = liftAt(bridges, x, z);
  for (let k = 0; k < 16; k++) y = Math.max(y, liftAt(bridges, x + Math.cos(k * Math.PI / 8) * r, z + Math.sin(k * Math.PI / 8) * r));
  return y;
}
